import { useCallback, useEffect, useMemo, useState } from "react";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { IIfcArtifacts, hasReadyIfcArtifacts } from "Data/interfaces/Viewer/IIfcArtifacts";
import { isWebGPUAvailable } from "Utils/webgpu";

export const extensions2dAnd3d = [
  'dwg', 'dxf', 'dgn', 'rvt', 'nwc', 'nwd',
  'ifc', 'dwf', 'dwfx', 'rcp',
];

export const extensions3d = [
  'rvt', 'dwf', 'nwc', 'nwd', 'ifc', 'dwfx',
  'rcp', 'skp',
];

export const allOtherExtensions = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'doc',
  'docx', 'docm', 'ppt', 'pps', 'ppsx', 'ppsm',
  'pptx', 'pptm', 'xls', 'xlsx', 'xlsm', 'rtf',
  'pdf', 'txt', 'html', 'gdoc', 'gsheet', 'gslides',
];

export const extensionsImage = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'webp',
  'gif',
];

export const extensionViews2d3d = ['rvt', 'dwf', 'dwfx', 'ifc', 'rcp', 'skp'];

export const extensionPranchas = ['rvt', 'dwf', 'dwfx'];

export const extensionViewpoints = ['nwc', 'nwd'];

export const COORDLY_2D_VIEWER_EXTENSIONS = ['dwg', 'dxf'];
export const COORDLY_3D_VIEWER_EXTENSIONS = ['ifc', 'gltf', 'glb', 'obj', 'stl'];

export const useCoordlyViewerFeature = () => {
  const coordlyViewerFeatureFlag = useFeatureFlag('coordly-2d-viewer');
  const isCoordlyViewerEnabled = coordlyViewerFeatureFlag.enabled && coordlyViewerFeatureFlag.value === 'test';

  const isCoordly2DViewerEligible = (fileExtension: string) => {
    return isCoordlyViewerEnabled && COORDLY_2D_VIEWER_EXTENSIONS.indexOf(fileExtension.toLocaleLowerCase()) !== -1;
  }

  // O 3D saiu daqui: tem flag própria (`coordly-3d-viewer`) e gate próprio.
  // Fonte única = useCoordly3DViewerFeature.
  const getCoordlyViewerType = (fileExtension: string): '2D' | null => {
    return isCoordly2DViewerEligible(fileExtension) ? '2D' : null;
  }

  return {
    getCoordlyViewerType,
    isCoordly2DViewerEligible,
  }
};

export const COORDLY_3D_VIEWER_FLAG = 'coordly-3d-viewer';

// Nesta fase o gate 3D vale só para IFC; as demais extensões de
// COORDLY_3D_VIEWER_EXTENSIONS são backlog do motor.
export const COORDLY_3D_VIEWER_EXTENSION = 'ifc';

/**
 * O teto de tamanho vem do próprio valor da flag, para ser afrouxado ou apertado
 * no Flagsmith sem deploy:
 *
 *  - `test`     → ligada, sem teto;
 *  - `test-100` → ligada, teto de 100 MB (o número é o limite em MB);
 *  - qualquer outro valor (ou flag desligada) → desligada.
 *
 * Acima do teto o arquivo segue o fluxo normal (Autodesk): o parse no server
 * ifc-lite e o streaming da geometria ainda não têm folga para modelos maiores,
 * e travar a aba do usuário é pior do que abrir no viewer antigo.
 */
export interface ICoordly3DFlag {
  isEnabled: boolean;
  /** `null` = sem teto. */
  maxSizeInBytes: number | null;
}

const COORDLY_3D_FLAG_VALUE_PATTERN = /^test(?:-(\d+))?$/;

export const parseCoordly3DFlag = (
  enabled?: boolean,
  value?: unknown,
): ICoordly3DFlag => {
  const match = COORDLY_3D_FLAG_VALUE_PATTERN.exec(String(value ?? '').trim());

  if (!enabled || !match) {
    return { isEnabled: false, maxSizeInBytes: null };
  }

  return {
    isEnabled: true,
    maxSizeInBytes: match[1] ? Number(match[1]) * 1024 * 1024 : null,
  };
};

export const COORDLY_3D_FLAG_OFF: ICoordly3DFlag = { isEnabled: false, maxSizeInBytes: null };

export type Coordly3DFallbackReason =
  | 'not-ifc'
  | 'no-webgpu'
  | 'flag-off'
  | 'file-too-large'
  | 'artifacts-missing';

export type Coordly3DDecision =
  | { target: 'server' }
  | { target: 'autodesk'; reason: Coordly3DFallbackReason };

export interface ICoordly3DInput {
  extension?: string;
  /**
   * Tamanho do arquivo em bytes. Quando não vier, o gate não bloqueia por
   * tamanho: preferimos deixar passar a barrar um arquivo pequeno por falta de
   * informação (o motor ainda tem o fallback de runtime).
   */
  sizeInBytes?: number;
  artifacts?: IIfcArtifacts | null;
}

export const isAboveCoordly3DSizeLimit = (
  sizeInBytes?: number,
  maxSizeInBytes?: number | null,
) => (
  typeof maxSizeInBytes === 'number'
  && typeof sizeInBytes === 'number'
  && sizeInBytes > maxSizeInBytes
);

/**
 * O parse é sempre no server. Dois caminhos, nesta ordem:
 *  1. artefatos prontos no CDN (`ifcArtifacts`), quando o backend .NET existir;
 *  2. parse sob demanda no server ifc-lite (`REACT_APP_IFC_SERVER_URL`) — o browser
 *     manda o .ifc e recebe a geometria tesselada.
 *
 * Antes disso, o tamanho: acima do teto que a flag declarou (`test-100` → 100 MB)
 * o arquivo cai para o Autodesk. Flag `test` puro não tem teto.
 *
 * O (2) é o que sustenta o gate hoje. O parse client-side foi descartado: quebra
 * arquivos (o single-thread do motor perde geometria) e o paralelo exigiria isolar
 * a origem, o que derruba a tela de Painéis. Sem artefatos E sem server, Autodesk.
 */
export const decideCoordly3DTarget = (
  flag: ICoordly3DFlag,
  hasWebGPU: boolean,
  input: ICoordly3DInput,
): Coordly3DDecision => {
  if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
    return { target: 'autodesk', reason: 'not-ifc' };
  }
  if (!hasWebGPU) { return { target: 'autodesk', reason: 'no-webgpu' }; }
  if (!flag.isEnabled) { return { target: 'autodesk', reason: 'flag-off' }; }
  if (isAboveCoordly3DSizeLimit(input.sizeInBytes, flag.maxSizeInBytes)) {
    return { target: 'autodesk', reason: 'file-too-large' };
  }

  return (hasReadyIfcArtifacts(input.artifacts) || !!process.env.REACT_APP_IFC_SERVER_URL)
    ? { target: 'server' }
    : { target: 'autodesk', reason: 'artifacts-missing' };
};

export const useCoordly3DViewerFeature = () => {
  const flag = useFeatureFlag(COORDLY_3D_VIEWER_FLAG);
  const coordly3DFlag = useMemo(
    () => parseCoordly3DFlag(flag?.enabled, flag?.value),
    [flag?.enabled, flag?.value],
  );
  const isCoordly3DViewerEnabled = coordly3DFlag.isEnabled;
  const [hasWebGPU, setHasWebGPU] = useState(false);

  // Aquece o probe: a decisão de abertura precisa dele resolvido ANTES de
  // navegar, e requestAdapter() é async. O resultado fica em state porque o
  // clique direito precisa decidir de forma síncrona (ver isCoordly3DViewerEligible).
  useEffect(() => {
    if (!isCoordly3DViewerEnabled) { return; }

    let alive = true;
    void isWebGPUAvailable().then((available) => {
      if (alive) { setHasWebGPU(available); }
    });

    return () => { alive = false; };
  }, [isCoordly3DViewerEnabled]);

  // Ordem do gate: ifc → WebGPU → flag → tamanho → artefatos.
  const decideCoordly3D = useCallback(async (input: ICoordly3DInput): Promise<Coordly3DDecision> => {
    if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
      return { target: 'autodesk', reason: 'not-ifc' };
    }
    return decideCoordly3DTarget(coordly3DFlag, await isWebGPUAvailable(), input);
  }, [coordly3DFlag]);

  /**
   * Versão síncrona do gate, para os pontos que não podem esperar o probe:
   * o clique direito precisa decidir dentro do próprio handler (um `await` aí
   * deixa a navegação cair depois do menu de contexto abrir) e o `href` do
   * nome do arquivo é calculado no render.
   *
   * Enquanto o probe não resolve, responde `false` e o arquivo segue o fluxo
   * Autodesk — mesmo fallback conservador do gate assíncrono.
   */
  const isCoordly3DViewerEligible = useCallback((extension?: string, sizeInBytes?: number) => (
    decideCoordly3DTarget(
      coordly3DFlag,
      hasWebGPU,
      { extension, sizeInBytes },
    ).target === 'server'
  ), [coordly3DFlag, hasWebGPU]);

  return { isCoordly3DViewerEnabled, decideCoordly3D, isCoordly3DViewerEligible };
};

/**
 * Extensões que o federado aceita incluir. Com a flag do viewer 3D ativa, só IFC:
 * o conversor automático da Autodesk está pausado até segunda ordem, então
 * qualquer outra extensão entraria como modelo que ninguém consegue renderizar.
 */
export const useFederatedUploadFilter = () => {
  const { isCoordly3DViewerEnabled } = useCoordly3DViewerFeature();

  const isUploadableExtension = useCallback((extension?: string) => (
    isCoordly3DViewerEnabled
      ? (extension || '').toLocaleLowerCase() === COORDLY_3D_VIEWER_EXTENSION
      : isValid3DExtension(extension || '')
  ), [isCoordly3DViewerEnabled]);

  return { isUploadableExtension, onlyIfc: isCoordly3DViewerEnabled };
};

export const isValid3DExtension = (extension: string) => {
  if (extensions3d.indexOf(extension.toLocaleLowerCase()) !== -1) {
    return true;
  }
  return false;
}

export const getFileExtension = (filename: string) => {
  var ext = /^.+\.([^.]+)$/.exec(filename);
  return ext == null ? "" : ext[1];
}
